import type { NoticeInput } from '../ui/notifications';
import { StudioIcon } from '../ui/StudioIcon';
import { useEffect, useState } from 'react';
import type { Item } from '@scribbleplot/shared';
import { Avatar, IconButton } from '../ui/primitives';
import { Dialog } from '../ui/overlays';
import { useMedia, shortcut } from '../ui/responsive';
import { SharePanel } from './SharePanel';
import { useEditorUI } from './ui';
import { useBoard } from './store';

export type TopBarProps = {
  name: string;
  accent: string;
  boardId: string;
  canEdit: boolean;
  /** Owner-only: sharing and collaborator management. */
  canManage: boolean;
  role: 'owner' | 'editor' | 'viewer';
  status: 'loading' | 'connecting' | 'live' | 'offline';
  peerCount: number;
  sidebarOpen: boolean;
  assetsOpen: boolean;
  items: Item[];
  isPublic: boolean;
  shareUrl: string;
  initials: string;
  onBack: () => void;
  onRename: (name: string) => void;
  onToggleSidebar: () => void;
  onToggleAssets: () => void;
  onTogglePublic: (isPublic: boolean) => void;
  onFocusItem: (id: string) => void;
  onOpenSettings: () => void;
  onExport: () => void;
  onCopy: (text: string, label: string) => void;
  onNotify: (message: NoticeInput) => void;
};

export function TopBar(props: TopBarProps) {
  const { name, accent, canEdit, canManage, role, onBack, onRename, onToggleSidebar, onToggleAssets, onExport } = props;
  const [title, setTitle] = useState(name);
  const [query, setQuery] = useState('');
  const desktop = useMedia('(min-width: 1200px)');
  const phone = useMedia('(max-width: 767px)');
  const { overlay, open } = useEditorUI();
  const board = useBoard();
  useEffect(() => setTitle(name), [name]);
  const commit = () => {
    const trimmed = title.trim();
    if (!trimmed) setTitle(name);
    else if (trimmed !== name) onRename(trimmed);
  };
  const matches = props.items
    .map((item) => ({ item, label: searchLabel(item) }))
    .filter(({ label }) => label.toLowerCase().includes(query.trim().toLowerCase()))
    .slice(0, 8);
  const status = board.sandbox
    ? 'Stored in this browser'
    : props.status === 'live'
      ? board.pendingCount || board.gesturing
        ? 'Syncing changes…'
        : 'Up to date'
      : props.status === 'offline'
        ? 'Reconnecting…'
        : 'Connecting…';
  const search = (
    <div className="sp-board-search">
      <input
        aria-label="Search this board"
        placeholder="Search board"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Escape') setQuery('');
          if (e.key === 'Enter' && matches[0]) {
            props.onFocusItem(matches[0].item.id);
            setQuery('');
            open(null);
          }
        }}
      />
      {query.trim() && (
        <div
          className={`sp-menu ${desktop ? 'sp-board-search-results' : ''}`}
          role="listbox"
          aria-label="Search results"
        >
          {matches.map(({ item, label }) => (
            <button
              key={item.id}
              role="option"
              aria-selected={false}
              onClick={() => {
                props.onFocusItem(item.id);
                setQuery('');
                open(null);
              }}
            >
              {label || 'Untitled item'}
            </button>
          ))}
          {!matches.length && <p className="sp-description">No matches on this board.</p>}
        </div>
      )}
    </div>
  );
  return (
    <>
      <header className="sp-board-topbar">
        <IconButton title="All boards" accent={accent} onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
            <path d="m10 3-5 5 5 5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </IconButton>
        <input
          className="sp-board-title"
          value={title}
          aria-label="Board name"
          readOnly={!canEdit}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') {
              setTitle(name);
              e.currentTarget.blur();
            }
          }}
        />
        {role !== 'owner' && (
          <span className="sp-desktop-only sp-description" style={{ margin: 0, fontSize: 11 }}>
            {canEdit ? 'SHARED' : 'VIEW ONLY'}
          </span>
        )}
        <div
          className="sp-board-status"
          aria-live="polite"
          title={board.sandbox ? status : `${status}${props.peerCount ? ` · ${props.peerCount} others here` : ''}`}
        >
          <i
            className="sp-status-dot"
            style={{ background: board.sandbox ? accent : props.status === 'live' ? '#83d7b2' : '#f6d56a' }}
          />
          <span>{status}</span>
          {!board.sandbox && props.peerCount > 0 && (
            <span>
              · {props.peerCount} other{props.peerCount === 1 ? '' : 's'} here
            </span>
          )}
        </div>
        <div className="sp-topbar-actions">
          {!phone && (
            <>
              {desktop ? (
                search
              ) : (
                <button className="sp-icon" aria-label="Search board" onClick={() => open('search')}>
                  <StudioIcon name="search" />
                </button>
              )}
              <IconButton title="Layers panel" accent={accent} active={props.sidebarOpen} onClick={onToggleSidebar}>
                <StudioIcon name="layers" />
              </IconButton>
              <IconButton title="Assets panel" accent={accent} active={props.assetsOpen} onClick={onToggleAssets}>
                <StudioIcon name="assets" />
              </IconButton>
              <IconButton title="Export this board" accent={accent} onClick={onExport}>
                <StudioIcon name="export" />
              </IconButton>
              {canManage && (
                <button className="sp-button sp-primary" onClick={() => open('share')}>
                  Share
                </button>
              )}
              {canEdit && (
                <div className="sp-history">
                  <button
                    className="sp-icon"
                    title={shortcut('Undo — ⌘Z')}
                    aria-label="Undo"
                    disabled={!board.past.length}
                    onClick={board.undo}
                  >
                    <StudioIcon name="undo" />
                  </button>
                  <button
                    className="sp-icon"
                    title={shortcut('Redo — ⇧⌘Z')}
                    aria-label="Redo"
                    disabled={!board.future.length}
                    onClick={board.redo}
                  >
                    <StudioIcon name="redo" />
                  </button>
                </div>
              )}
              {props.initials && (
                <Avatar initials={props.initials} accent={accent} onClick={props.onOpenSettings} title="Settings" />
              )}
            </>
          )}
          {phone && (
            <button className="sp-icon" aria-label="Board actions" onClick={() => open('actions')}>
              •••
            </button>
          )}
        </div>
      </header>
      {overlay === 'search' && (
        <Dialog title="Search board" drawer onClose={() => open(null)}>
          {search}
        </Dialog>
      )}
      {overlay === 'actions' && (
        <Dialog title="Board actions" drawer onClose={() => open(null)}>
          <p className="sp-description">{status}</p>
          <div className="sp-menu">
            <button onClick={() => open('search')}>Search board</button>
            <button onClick={() => open('layers')}>Layers panel</button>
            <button onClick={() => open('assets')}>Assets panel</button>
            {canManage && <button onClick={() => open('share')}>Share</button>}
            <button
              onClick={() => {
                open(null);
                onExport();
              }}
            >
              Export this board <small>Board file</small>
            </button>
            {props.initials && (
              <button
                onClick={() => {
                  open(null);
                  props.onOpenSettings();
                }}
              >
                Settings
              </button>
            )}
          </div>
        </Dialog>
      )}
      {overlay === 'share' && canManage && (
        <Dialog title="Share board" drawer onClose={() => open(null)}>
          <div className="sp-editor-drawer-content">
            <SharePanel
              boardId={props.boardId}
              accent={accent}
              isPublic={props.isPublic}
              shareUrl={props.shareUrl}
              onTogglePublic={props.onTogglePublic}
              onCopy={props.onCopy}
              onNotify={props.onNotify}
            />
          </div>
        </Dialog>
      )}
    </>
  );
}
function searchLabel(item: Item): string {
  switch (item.type) {
    case 'sticky':
    case 'text':
      return item.text;
    case 'image':
    case 'group':
      return item.name;
    case 'frame':
      return item.label;
    case 'link':
      return `${item.title} ${item.url}`;
  }
}
